/**
 * Phase 3 — export from an already-rendered preview raster (no generator re-exec).
 * Falls back to callers providing an iframe-rendered canvas when preview is unavailable.
 */

import {
  buildExportFilename,
  embedExportMetadata,
  resolveExportIdentity,
  resolveExportPermalinkToken,
} from './exportIdentity.ts';

export type ArtExportSource = {
  html?: string;
  config?: Record<string, unknown> & { seed?: string; description?: string };
  previewUrl?: string;
  displayUrl?: string;
  previewPending?: boolean;
};

export type ExportEncodeOptions = {
  mimeType: string;
  quality?: number;
  fileExtension: string;
  /** Optional post-download side effect (e.g. TIFF success alert). */
  onSuccess?: () => void;
};

/** Prefer displayUrl, then previewUrl. Skip while preview is still pending. */
export function getArtPreviewSourceUrl(art: ArtExportSource | null | undefined): string | null {
  if (!art || art.previewPending) return null;
  const url = art.displayUrl || art.previewUrl;
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  // Accept blob:, data:, http(s): — reject obviously broken values
  if (
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://')
  ) {
    return trimmed;
  }
  return null;
}

export type DecodedPreviewSource = {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
};

/**
 * Decode an existing preview PNG/raster URL without running the generator.
 * Temporary ImageBitmap (preferred) or HTMLImageElement; caller must close().
 */
export async function decodePreviewSource(url: string): Promise<DecodedPreviewSource> {
  if (typeof createImageBitmap === 'function') {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch preview: ${response.status}`);
      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error('Empty preview blob');
      const bitmap = await createImageBitmap(blob);
      if (!bitmap.width || !bitmap.height) {
        bitmap.close();
        throw new Error('Decoded preview has zero dimensions');
      }
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => {
          try { bitmap.close(); } catch { /* ignore */ }
        },
      };
    } catch {
      // Fall through to HTMLImageElement decode
    }
  }

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Failed to decode preview image'));
    el.src = url;
  });
  if (!img.naturalWidth || !img.naturalHeight) {
    throw new Error('Decoded preview has zero dimensions');
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    close: () => {
      try { elRevokeIfNeeded(img); } catch { /* ignore */ }
    },
  };
}

function elRevokeIfNeeded(_img: HTMLImageElement): void {
  // Image element has no close(); GC handles it after we drop references.
}

/** Scale any CanvasImageSource into a new canvas at the requested export size. */
export function scaleSourceToExportCanvas(
  source: CanvasImageSource,
  size: number
): HTMLCanvasElement {
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = size;
  exportCanvas.height = size;
  const ctx = exportCanvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  ctx.drawImage(source, 0, 0, size, size);
  return exportCanvas;
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob from canvas'));
      },
      mimeType,
      quality
    );
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type PreparedExportCanvas = {
  canvas: HTMLCanvasElement;
  /** True when scaled from previewUrl/displayUrl (no art.html execution). */
  usedPreviewReuse: boolean;
  sourceWidth: number | null;
  sourceHeight: number | null;
};

/**
 * Prefer existing preview raster; return null so the caller can fall back to iframe regeneration.
 */
export async function tryPrepareExportCanvasFromPreview(
  art: ArtExportSource,
  size: number
): Promise<PreparedExportCanvas | null> {
  const previewUrl = getArtPreviewSourceUrl(art);
  if (!previewUrl) return null;

  let decoded: DecodedPreviewSource | null = null;
  try {
    decoded = await decodePreviewSource(previewUrl);
    const canvas = scaleSourceToExportCanvas(decoded.source, size);
    return {
      canvas,
      usedPreviewReuse: true,
      sourceWidth: decoded.width,
      sourceHeight: decoded.height,
    };
  } catch {
    return null;
  } finally {
    if (decoded) decoded.close();
  }
}

/**
 * Legacy path: load art.html in a hidden iframe, wait for paint, scale canvas to export size.
 * Used only when preview reuse is unavailable.
 */
export async function prepareExportCanvasViaIframe(
  art: ArtExportSource,
  size: number
): Promise<PreparedExportCanvas> {
  if (!art.html) throw new Error('No art HTML available for export fallback');

  const tempIframe = document.createElement('iframe');
  tempIframe.style.position = 'absolute';
  tempIframe.style.left = '-9999px';
  tempIframe.style.width = `${size}px`;
  tempIframe.style.height = `${size}px`;
  tempIframe.style.border = 'none';
  tempIframe.style.transform = 'scale(1)';
  tempIframe.style.transformOrigin = 'top left';

  document.body.appendChild(tempIframe);
  try {
    await new Promise<void>((resolve) => {
      tempIframe.onload = () => resolve();
      tempIframe.srcdoc = art.html as string;
    });

    const iframeDoc = tempIframe.contentDocument || tempIframe.contentWindow?.document;
    if (!iframeDoc) throw new Error('Could not access iframe document');

    const canvas = iframeDoc.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('No canvas found in art');

    // Preserve existing wait so the embedded algorithm can finish painting.
    await new Promise((resolve) => {
      window.setTimeout(resolve, 200);
    });

    const exportCanvas = scaleSourceToExportCanvas(canvas, size);
    return {
      canvas: exportCanvas,
      usedPreviewReuse: false,
      sourceWidth: canvas.width,
      sourceHeight: canvas.height,
    };
  } finally {
    if (tempIframe.parentNode) {
      document.body.removeChild(tempIframe);
    }
  }
}

/** Preview reuse first; iframe regeneration fallback. */
export async function prepareExportCanvas(
  art: ArtExportSource,
  size: number
): Promise<PreparedExportCanvas> {
  const fromPreview = await tryPrepareExportCanvasFromPreview(art, size);
  if (fromPreview) return fromPreview;
  return prepareExportCanvasViaIframe(art, size);
}

export async function exportArtAtSize(
  art: ArtExportSource,
  size: number,
  options: ExportEncodeOptions
): Promise<{ usedPreviewReuse: boolean; sourceWidth: number | null; sourceHeight: number | null }> {
  const prepared = await prepareExportCanvas(art, size);
  let blob = await canvasToBlob(prepared.canvas, options.mimeType, options.quality);

  const identity = resolveExportIdentity(art.config);
  const permalinkToken = identity?.token ?? resolveExportPermalinkToken(art.config);
  const filename = buildExportFilename(
    permalinkToken,
    size,
    options.fileExtension,
    art.config?.seed
  );

  const metaResult = await embedExportMetadata(blob, options.mimeType, identity?.metadata);
  blob = metaResult.blob;

  downloadBlob(blob, filename);
  if (options.onSuccess) options.onSuccess();
  return {
    usedPreviewReuse: prepared.usedPreviewReuse,
    sourceWidth: prepared.sourceWidth,
    sourceHeight: prepared.sourceHeight,
  };
}
